import classNames from 'classnames';
import {
  addDays,
  addHours,
  addMinutes,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  isAfter,
} from 'date-fns';
import type { ComponentProps, ReactNode } from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import type { IStyledComponent } from 'styled-components';
import styled from 'styled-components';

import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import type { Props as TypographyProps } from '#src/atoms/typography/index.tsx';
import { HeadlineXL, LabelS } from '#src/atoms/typography/index.tsx';
import { useTickerContext } from '#src/contexts/ticker/index.tsx';

const CountdownWrapper = styled(Horizontal)`
  align-self: center;

  & > * {
    width: 2ch;
  }
`;
const Wrapper = styled(Vertical)`
  &.disabled {
    opacity: 0.5;
  }
`;

const StyledHeadlineXL = styled(HeadlineXL)`
  font: var(--t-48);
  font-variant-numeric: tabular-nums;
`;
const StyledLabelS = styled(LabelS)`
  text-transform: none;
  color: var(--c-onBase-secondary);
`;

type CountdownUnit = 'day' | 'hour' | 'minute' | 'second';

type Props = {
  date: Date;
  Label?: IStyledComponent<'web', TypographyProps>;
  Caption?: IStyledComponent<'web', TypographyProps>;
  greyOutZeros?: boolean;
  gap?: ComponentProps<typeof Horizontal>['gap'];
  separator?: ReactNode;
  onEnded?: () => void;
  leftPad?: boolean;
};

export const Countdown = ({
  date,
  Label = StyledHeadlineXL,
  Caption = StyledLabelS,
  greyOutZeros,
  gap = 3,
  separator,
  onEnded,
  leftPad,
}: Props) => {
  const { now } = useTickerContext();
  const { formatNumberToParts } = useIntl();
  const daysLeft = differenceInDays(date, now);
  const addedDays = addDays(now, daysLeft);
  const hoursLeft = differenceInHours(date, addedDays);
  const addedHours = addHours(addedDays, hoursLeft);
  const minutesLeft = differenceInMinutes(date, addedHours);
  const addedMinutes = addMinutes(addedHours, minutesLeft);
  const secondsLeft = differenceInSeconds(date, addedMinutes);
  const isEnded = isAfter(now, date);

  useEffect(() => {
    if (isEnded && onEnded) onEnded();
  }, [isEnded, onEnded]);

  const format = (n: number): number | string =>
    leftPad ? String(n).padStart(2, '0') : n;

  const formatUnit = (count: number, unit: CountdownUnit) =>
    formatNumberToParts(count, { style: 'unit', unit, unitDisplay: 'long' })
      .filter(part => part.type === 'unit')
      .map(part => part.value)
      .join('');

  if (isEnded) {
    return null;
  }

  return (
    <CountdownWrapper gap={gap}>
      <Label as="div">
        <Wrapper
          gap={0}
          center
          className={classNames({ disabled: greyOutZeros && daysLeft === 0 })}
        >
          {format(daysLeft)}
          <Caption as="span">{formatUnit(daysLeft, 'day')}</Caption>
        </Wrapper>
      </Label>
      {separator}
      <Label as="div">
        <Wrapper
          gap={0}
          center
          className={classNames({
            disabled: greyOutZeros && daysLeft === 0 && hoursLeft === 0,
          })}
        >
          {format(hoursLeft)}
          <Caption as="span">{formatUnit(hoursLeft, 'hour')}</Caption>
        </Wrapper>
      </Label>
      {separator}
      <Label as="div">
        <Wrapper
          gap={0}
          center
          className={classNames({
            disabled:
              greyOutZeros &&
              daysLeft === 0 &&
              hoursLeft === 0 &&
              minutesLeft === 0,
          })}
        >
          {format(minutesLeft)}
          <Caption as="span">{formatUnit(minutesLeft, 'minute')}</Caption>
        </Wrapper>
      </Label>
      {separator}
      <Label as="div">
        <Wrapper gap={0} center>
          {format(secondsLeft)}
          <Caption as="span">{formatUnit(secondsLeft, 'second')}</Caption>
        </Wrapper>
      </Label>
    </CountdownWrapper>
  );
};
